import { axios } from "..";
import { IRegistration } from "../interfaces/models/IRegistration";
import { IDashboardData } from "../interfaces/models/IDashboardData";
import { MemberEventAttendee } from "../interfaces/models/IMemberEventAttendee";
import { getWSGFirmIds } from "./Firm";
import { normalizeRegistrationGuests } from "../utils/registrationGuests";

export const getMemberEventAttendees = async (
  eventId: string,
): Promise<MemberEventAttendee[]> => {
  const response = await axios.Get(
    `./registration?action=memberEventAttendees&eventId=${encodeURIComponent(eventId)}`,
  );
  return (response.data ?? []) as MemberEventAttendee[];
};

export const getDashboardAttendees = async (
  eventId: string,
): Promise<IDashboardData> => {
  const response = await axios.Get(`./attendeesDashboard?eventId=${eventId}`);
  return response.data as IDashboardData;
};

export const getRegistrationById = async (
  id: string,
): Promise<IRegistration> => {
  const params = new URLSearchParams();
  params.append(
    "filter",
    JSON.stringify({ id: { value: id, comparisonType: "EQUAL" } }),
  );
  const response = await axios.Get(`./registration?${params.toString()}`);
  return response.data[0];
};

export const createRegistration = async (
  registration: IRegistration,
): Promise<any> => {
  const response = await axios.Post("./registration", registration);
  return response.data;
};

export const updateRegistration = async (registration: any): Promise<any> => {
  const response = await axios.PutDataIntoModel("./registration", registration);
  return response.data;
};

/** Staff cancel: server only persists cancelRegistrationDate (+ derived status). */
export const updateRegistrationCancelOnly = async (
  registrationId: string,
  cancelRegistrationDate: number,
  options?: {
    changedBy?: string;
    changedById?: string | null;
  },
): Promise<any> => {
  const response = await axios.PutDataIntoModel("./registration", {
    id: registrationId,
    cancelRegistrationDate,
    cancelRegistrationOnly: true,
    guestActionBy: options?.changedBy,
    guestActionById: options?.changedById,
  });
  return response.data;
};

/** Staff restore: clears cancelRegistrationDate and sets status back to attending. */
export const updateRegistrationRestoreOnly = async (
  registrationId: string,
  options?: {
    changedBy?: string;
    changedById?: string | null;
  },
): Promise<any> => {
  const response = await axios.PutDataIntoModel("./registration", {
    id: registrationId,
    restoreRegistrationOnly: true,
    guestActionBy: options?.changedBy,
    guestActionById: options?.changedById,
  });
  return response.data;
};

/** Staff attendee edit: persist only guests[] on an existing registration. */
export const updateRegistrationGuestOnly = async (
  registrationId: string,
  guestFields: {
    guests?: IRegistration["guests"];
    /** @deprecated Prefer guests */
    guestName?: string;
    /** @deprecated Prefer guests */
    guestName2?: string;
    guestActionBy?: string;
  },
): Promise<any> => {
  const response = await axios.PutDataIntoModel("./registration", {
    id: registrationId,
    ...guestFields,
    registrationGuestOnly: true,
  });
  return response.data;
};

export const deleteRegistration = async (id: string): Promise<boolean> => {
  await axios.Delete("./registration", id);
  return true;
};

export const getRegistrationsByEvent = async (
  eventId: string,
): Promise<IRegistration[]> => {
  try {
    const params = new URLSearchParams();
    params.append(
      "filter",
      JSON.stringify({ eventId: { value: eventId, comparisonType: "EQUAL" } }),
    );
    const response = await axios.Get(`./registration?${params.toString()}`);
    return response.data;
  } catch {
    return [];
  }
};

export const getRegistrationsByPerson = async (
  personId: string,
): Promise<IRegistration[]> => {
  try {
    const params = new URLSearchParams();
    params.append(
      "filter",
      JSON.stringify({
        personId: {
          value: personId,
          comparisonType: "EQUAL",
        },
      }),
    );
    const response = await axios.Get(`./registration?${params.toString()}`);
    if (Array.isArray(response.data) && response.data.length > 0) {
      return response.data;
    }

    const legacyParams = new URLSearchParams();
    legacyParams.append(
      "filter",
      JSON.stringify({
        "person.personId": {
          value: personId,
          comparisonType: "EQUAL",
        },
      }),
    );

    const legacyResponse = await axios.Get(
      `./registration?${legacyParams.toString()}`,
    );
    return legacyResponse.data;
  } catch {
    return [];
  }
};

export const getRegistrationByPersonAndEvent = async (
  personId: string,
  eventId: string,
): Promise<IRegistration | null> => {
  try {
    const params = new URLSearchParams();
    params.append(
      "filter",
      JSON.stringify({
        personId: {
          value: personId,
          comparisonType: "EQUAL",
        },
        eventId: { value: eventId, comparisonType: "EQUAL" },
      }),
    );
    const response = await axios.Get(`./registration?${params.toString()}`);
    return response.data?.[0] || null;
  } catch {
    return null;
  }
};

export const getAttendeeCountsByEvent = async (
  eventId: string,
): Promise<{
  regular: number;
  sponsor: number;
  guest: number;
  total: number;
}> => {
  try {
    const allRegistrations = await getRegistrationsByEvent(eventId);
    const registrations = allRegistrations.filter((reg: any) => !reg.isDeleted);

    return registrations.reduce(
      (acc, registration) => {
        const attendeeType =
          registration.attendeeType ||
          (registration as any).person?.attendeeType ||
          "regular";

        const hasGuest = normalizeRegistrationGuests(registration).length > 0;
        if (hasGuest) {
          acc.guest++;
        }

        switch (attendeeType) {
          case "regular":
            acc.regular++;
            break;
          case "sponsor":
            acc.sponsor++;
            break;
        }
        acc.total++;
        return acc;
      },
      { regular: 0, sponsor: 0, guest: 0, total: 0 },
    );
  } catch {
    return { regular: 0, sponsor: 0, guest: 0, total: 0 };
  }
};

/** Count all attendees except WSG Staff. Includes: regular, sponsors, guest speakers, guests, invited guests. */
const STAFF_FIRM_IDS = [
  "ee153da4-bbf0-4c29-80cb-29a05905ae2c",
  "430be9f8-c9be-4f62-aec1-91e0596799b8",
  "7cef821b-c7f7-40a8-bf7c-93aab29ac54d",
];

function isPaidRegistrant(registration: any): boolean {
  const firmId = registration.personId?.firm || registration.firmId || "";
  if (STAFF_FIRM_IDS.includes(firmId)) {
    return false;
  } // Exclude WSG Staff only
  return true; // Include regular, sponsors, guest speakers, guests, invited guests
}

export const getAttendeeCountsByEvents = async (
  eventIds: string[],
): Promise<
  Record<
    string,
    {
      regular: number;
      sponsor: number;
      guest: number;
      total: number;
      paidRegistrants: number;
      unpaidRegistrants: number;
    }
  >
> => {
  try {
    const counts = eventIds.reduce(
      (acc, eventId) => {
        acc[eventId] = {
          regular: 0,
          sponsor: 0,
          guest: 0,
          total: 0,
          paidRegistrants: 0,
          unpaidRegistrants: 0,
        };
        return acc;
      },
      {} as Record<
        string,
        {
          regular: number;
          sponsor: number;
          guest: number;
          total: number;
          paidRegistrants: number;
          unpaidRegistrants: number;
        }
      >,
    );

    const params = new URLSearchParams();
    params.append(
      "filter",
      JSON.stringify({
        eventId: {
          value: eventIds,
          comparisonType: "IN",
        },
      }),
    );

    const response = await axios.Get(`./registration?${params.toString()}`);
    const allRegistrations: IRegistration[] = response.data || [];
    const registrations = allRegistrations.filter((reg: any) => !reg.isDeleted);
    const wsgFirmIds = await getWSGFirmIds();
    registrations.forEach((registration) => {
      const { eventId } = registration;
      const attendeeType =
        registration.attendeeType ||
        (registration as any).person?.attendeeType ||
        "regular";
      const status = registration.status || "attending";
      if (status === "cancelled") {
        return;
      }
      if (
        counts[eventId] &&
        registration.firmId &&
        !wsgFirmIds.includes(registration.firmId!)
      ) {
        const hasGuest = normalizeRegistrationGuests(registration).length > 0;
        const isUnpaid =
          registration.paymentStatus === "pending" ||
          registration.paymentStatus === "unpaidSPR" ||
          registration.paymentStatus === "unpaidSPK" ||
          registration.paymentStatus === "partial";
        if (isUnpaid) {
          counts[eventId].unpaidRegistrants++;
        }
        if (hasGuest) {
          counts[eventId].guest++;
        }

        switch (attendeeType) {
          case "regular":
            counts[eventId].regular++;
            break;
          case "sponsor":
            counts[eventId].sponsor++;
            break;
        }
        counts[eventId].total++;
        if (isPaidRegistrant(registration)) {
          counts[eventId].paidRegistrants++;
        }
      }
    });

    return counts;
  } catch {
    return {};
  }
};

export const sendRegistrationEmail = async (
  registrationId: string,
  eventId: string,
  pdfPath?: string,
  usePaidTemplate?: boolean,
): Promise<any> => {
  try {
    const response = await axios.Post(
      "./registration/send-registration-email",
      {
        registrationId,
        eventId,
        pdfPath,
        usePaidTemplate,
      },
    );
    return response.data;
  } catch (error: any) {
    console.error("[Registration Service] Error sending registration email:", {
      registrationId,
      eventId,
      error: error?.response?.data || error?.message,
      status: error?.response?.status,
    });
    return null;
  }
};

export const checkPrepaidStatus = async (
  eventId: string,
  personId: string,
  registrationId?: string,
): Promise<boolean> => {
  try {
    const response = await axios.Get(
      `./registration?action=checkPrepaidStatus&eventId=${eventId}&personId=${personId}&registrationId=${registrationId}`,
    );
    return response.data?.isPrepaid === true;
  } catch (error: any) {
    console.error("[Registration Service] Error checking prepaid status:", {
      eventId,
      personId,
      registrationId,
      error: error?.response?.data || error?.message,
      status: error?.response?.status,
    });
    return false;
  }
};

export type HostFirmStatusResult = {
  isHostFirm: boolean;
  isPaidSponsor: boolean;
};

export const checkHostFirmStatus = async (
  eventId: string,
  personId: string,
  registrationId?: string,
): Promise<HostFirmStatusResult> => {
  try {
    const registrationQuery = registrationId
      ? `&registrationId=${encodeURIComponent(registrationId)}`
      : "";
    const response = await axios.Get(
      `./registration?action=checkHostFirmStatus&eventId=${eventId}&personId=${personId}${registrationQuery}`,
    );
    return {
      isHostFirm: response.data?.isHostFirm === true,
      isPaidSponsor: response.data?.isPaidSponsor === true,
    };
  } catch (error: any) {
    console.error("[Registration Service] Error checking host firm status:", {
      eventId,
      personId,
      registrationId,
      error: error?.response?.data || error?.message,
      status: error?.response?.status,
    });
    return { isHostFirm: false, isPaidSponsor: false };
  }
};
